<script lang="ts">
  import { acceptOffer, dismissOffer, newPlaces, places } from '../state/places.svelte'
  import { layout } from '../state/layout.svelte'
  import Icon from './Icon.svelte'

  /**
   * "Add 2 places?" (Phase 9 plan §6.7), as round 2 draws it (3.7): a dialog
   * on the desktop and a sheet on a phone, one row per place in the link,
   * each ticked, and the ones already saved shown, unticked and dead, so the
   * count on the button is what will actually be added.
   *
   * A link is untrusted input, so it is offered rather than applied, merged
   * rather than substituted, and its names are rendered as text, never as
   * markup.
   */

  let offer = $derived(places.offer ?? [])
  let fresh = $derived(places.offer ? newPlaces(places.offer) : [])

  /** Indexes into `offer` the user has unticked. */
  let unticked = $state<number[]>([])

  // A new offer starts with every new place ticked.
  $effect(() => {
    void places.offer
    unticked = []
  })

  let rows = $derived(
    offer.map((place, i) => {
      const isNew = fresh.includes(place)
      return { place, i, isNew, on: isNew && !unticked.includes(i) }
    }),
  )
  let chosen = $derived(rows.filter((r) => r.on).map((r) => r.place))

  function toggle(i: number) {
    unticked = unticked.includes(i) ? unticked.filter((j) => j !== i) : [...unticked, i]
  }

  let count = $derived(`${chosen.length} place${chosen.length === 1 ? '' : 's'}`)
</script>

{#if places.offer && fresh.length > 0}
  <!-- svelte-ignore a11y_click_events_have_key_events, a11y_no_static_element_interactions -->
  <div class="scrim" onclick={dismissOffer}></div>
  <div
    class="offer"
    class:phone={!layout.isDesktop}
    role="dialog"
    aria-modal="true"
    aria-label="Places from a link"
  >
    {#if !layout.isDesktop}<span class="grab" aria-hidden="true"></span>{/if}
    <div class="head">
      <span class="eyebrow from">From a shared link</span>
      <span class="ask">Add {count}?</span>
    </div>
    <ul class="rows">
      {#each rows as row (row.i)}
        <li class:dup={!row.isNew}>
          <label>
            <input
              class="wx-box"
              type="checkbox"
              checked={row.on}
              disabled={!row.isNew}
              onchange={() => toggle(row.i)}
            />
            <span class="name">{row.place.name}</span>
            <span class="sub">{row.isNew ? '' : 'Already saved'}</span>
          </label>
        </li>
      {/each}
    </ul>
    <div class="actions">
      <button
        type="button"
        class="wx-btn ink touch-target"
        disabled={chosen.length === 0}
        onclick={() => acceptOffer(chosen)}
      >
        <Icon name="check" size={16} strokeWidth={3} />Add {count}
      </button>
      <button type="button" class="wx-btn quiet touch-target" onclick={dismissOffer}>Not now</button>
    </div>
  </div>
{/if}

<style>
  .scrim {
    position: fixed;
    inset: 0;
    z-index: 44;
    background: rgb(0 0 0 / 45%);
  }

  .offer {
    position: fixed;
    z-index: 45;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: min(440px, calc(100vw - 32px));
    max-height: calc(100dvh - 64px);
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    background: var(--color-bg);
    color: var(--color-text);
    border-top: 4px solid var(--color-text);
    box-shadow: var(--shadow-lg);
    --pad: 24px;
    --btn-h: 40px;
    --btn-fs: 14px;
  }

  /* A sheet from the bottom edge on a phone, the thumb's end of the screen. */
  .offer.phone {
    top: auto;
    left: 0;
    right: 0;
    bottom: 0;
    transform: none;
    width: auto;
    max-height: 85dvh;
    border-top-width: 2px;
    padding-bottom: var(--safe-bottom);
    --pad: 20px;
    --btn-h: 48px;
    --btn-fs: 15px;
  }

  .grab {
    align-self: center;
    width: 32px;
    height: 4px;
    margin-top: 8px;
    background: var(--color-divider);
  }

  .head {
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 22px var(--pad) 14px;
  }

  .phone .head {
    padding-top: 14px;
    gap: 4px;
  }

  .from {
    color: var(--color-muted);
  }

  .ask {
    font-size: 22px;
    font-weight: 800;
  }

  .rows {
    list-style: none;
    margin: 0;
    padding: 0 var(--pad);
  }

  li {
    border-top: 1px solid var(--color-divider);
  }

  label {
    display: grid;
    grid-template-columns: 30px minmax(0, 1fr) auto;
    align-items: center;
    min-height: 48px;
    cursor: pointer;
  }

  .phone label {
    grid-template-columns: 34px minmax(0, 1fr) auto;
    min-height: 56px;
  }

  label :global(.wx-box) {
    width: 18px;
    height: 18px;
  }

  .phone label :global(.wx-box) {
    width: 22px;
    height: 22px;
  }

  /* Already saved: a dashed, empty box, and the name at half strength. */
  .dup label {
    cursor: default;
  }

  .dup label :global(.wx-box) {
    border: 2px dashed var(--color-divider);
    background: none;
  }

  .dup .name {
    opacity: 0.5;
  }

  .name {
    font-size: 14px;
    font-weight: 800;
    overflow-wrap: anywhere;
  }

  .phone .name {
    font-size: 16px;
  }

  .sub {
    font-size: 12px;
    color: var(--color-muted);
  }

  .actions {
    display: flex;
    gap: 8px;
    margin-top: 4px;
    padding: 16px var(--pad) 22px;
    border-top: 2px solid var(--color-divider);
  }

  .phone .actions {
    display: grid;
    grid-template-columns: 1fr 1fr;
    padding-bottom: 20px;
  }

  .phone .actions .wx-btn {
    justify-content: center;
  }
</style>
